import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fornecedorAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import type { Fornecedor } from "@/types/fornecedor";
import { fornecedorSchema } from "@/schemas/tabelasAuxiliaresSchemas";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  item: Fornecedor | null;
  onSuccess: () => void;
}

export function FornecedorFormDialog({ open, onOpenChange, mode, item, onSuccess }: Props) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(fornecedorSchema),
  });

  useEffect(() => {
    if (open) {
      if (mode === "edit" && item) {
        reset({
          nome: item.nome,
          cnpj_cpf: item.cnpj_cpf || "",
          telefone: item.telefone || "",
          email: item.email || "",
          endereco: item.endereco || "",
          observacoes: item.observacoes || "",
        });
      } else {
        reset({ nome: "", cnpj_cpf: "", telefone: "", email: "", endereco: "", observacoes: "" });
      }
    }
  }, [open, mode, item, reset]);

  const onSubmit = async (data: any) => {
    try {
      if (mode === "create") {
        await fornecedorAdapter.create(data);
      } else if (item) {
        await fornecedorAdapter.update(item.id_fornecedor, data);
      }
      onSuccess();
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível salvar.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Novo Fornecedor" : "Editar Fornecedor"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>Nome *</Label>
            <Input {...register("nome")} required />
            {errors.nome && <p className="text-sm text-destructive mt-1">{errors.nome.message as string}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>CNPJ/CPF</Label>
              <Input {...register("cnpj_cpf")} />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input {...register("telefone")} />
            </div>
          </div>
          <div>
            <Label>E-mail</Label>
            <Input type="email" {...register("email")} />
            {errors.email && <p className="text-sm text-destructive mt-1">{errors.email.message as string}</p>}
          </div>
          <div>
            <Label>Endereço</Label>
            <Textarea {...register("endereco")} />
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea {...register("observacoes")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
